'use client';

import { AppImageClient, AppImageData } from '@components/appImage';
import { CTAButtons } from '@components/ctaButtons';
import {
  Typography,
  TYPOGRAPHY_CLASS_BY_VARIANT_MAP,
  TypographyVariant,
} from '@components/typography';
import { classNames } from '@lib/classNames/classNames';
import { cn } from '@lib/utils/cn';
import { memo } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';

import { Sanitizer } from '../sanitizer';

import {
  Button,
  ButtonParams,
  ButtonSpacing,
  ButtonTarget,
} from '@/shared/components/button';
import { Card } from '@/shared/components/card';

export interface CTACard {
  id: string;
  type?: 'CTA';
  title?: string;
  titleVariant?: TypographyVariant;
  description?: string;
  descriptionVariant?: TypographyVariant;
  mainButton?: ButtonParams;
  secondaryButton?: ButtonParams;
  colSpan?: number;
  height?: number;
}

export interface CommonCard {
  id: string;
  title?: string;
  titleVariant?: TypographyVariant;
  description?: string;
  descriptionVariant?: TypographyVariant;
  icon: React.ReactNode;
  buttonLabel?: string;
  buttonHref?: string;
  buttonTarget?: ButtonTarget;
  buttonSpacing?: ButtonSpacing;
  buttonClassName?: string;
  colSpan?: number;
  height?: number;
}

const isCtaCard = (card: WhyChoosePlatformCard): card is CTACard =>
  'type' in card && card.type === 'CTA';

export interface GridConfig {
  lg?: number;
  md?: number;
  sm?: number;
}

export type WhyChoosePlatformCard = CommonCard | CTACard;

interface WhyChoosePlatformProps {
  cards: WhyChoosePlatformCard[];
  iconsWrapperClassName?: string;
  /** Готовый signed URL декоративной CTA-картинки. Legacy — используйте ctaImageData. */
  ctaImageUrl?: string;
  ctaMobileImageUrl?: string;
  /** Данные AppImage для CTA-картинки из `getAppImageData`. Приоритетнее ctaImageUrl. */
  ctaImageData?: AppImageData;
  ctaMobileImageData?: AppImageData;
  mobileCardHeight?: string | number;
  desktopCardHeight?: string | number;
  customCard?: React.ReactNode;
  gridConfig?: GridConfig;
  /** Отступы мобильного слайдера — задают, насколько виден следующий слайд */
  mobileSliderClassName?: string;
  ctaImageClassName?: string;
  ctaMobileImageClassName?: string;
}

/**
 * Нет смысла делать через инлайн стили
 * Маппинг для явных Tailwind классов
 */
const GRID_COLS_MAP: Record<number, string> = {
  1: 'grid-cols-1',
  2: 'grid-cols-2',
  3: 'grid-cols-3',
  4: 'grid-cols-4',
  5: 'grid-cols-5',
  6: 'grid-cols-6',
};

const GRID_MD_COLS_MAP: Record<number, string> = {
  1: 'md:grid-cols-1',
  2: 'md:grid-cols-2',
  3: 'md:grid-cols-3',
  4: 'md:grid-cols-4',
  5: 'md:grid-cols-5',
  6: 'md:grid-cols-6',
};

const GRID_LG_COLS_MAP: Record<number, string> = {
  1: 'lg:grid-cols-1',
  2: 'lg:grid-cols-2',
  3: 'lg:grid-cols-3',
  4: 'lg:grid-cols-4',
  5: 'lg:grid-cols-5',
  6: 'lg:grid-cols-6',
};

export const WhyChoosePlatform = memo(
  ({
    cards,
    iconsWrapperClassName,
    ctaImageUrl,
    ctaMobileImageUrl,
    ctaImageData,
    ctaMobileImageData,
    mobileCardHeight = '240px',
    desktopCardHeight = '289px',
    customCard,
    gridConfig,
    mobileSliderClassName = 'ml-[-8px]! pr-14! pl-6!',
    ctaImageClassName,
    ctaMobileImageClassName,
  }: WhyChoosePlatformProps) => {
    // Без gridConfig — стандартная сетка 1/2/3
    const smCols = gridConfig?.sm ?? 1;
    const mdCols = gridConfig?.md ?? 2;
    const lgCols = gridConfig?.lg ?? 3;

    const gridClasses = classNames({
      cls: 'grid gap-9 px-4 max-md:hidden',
      additional: [
        GRID_COLS_MAP[smCols],
        GRID_MD_COLS_MAP[mdCols],
        GRID_LG_COLS_MAP[lgCols],
      ],
    });

    return (
      <>
        {/* ----------- Мобильная версия ----------- */}
        <div className="flex md:hidden" style={{ minHeight: mobileCardHeight }}>
          <Swiper
            spaceBetween={16}
            slidesPerView="auto"
            className={mobileSliderClassName}
          >
            {cards?.map((card) => {
              if (isCtaCard(card)) {
                return (
                  <SwiperSlide key={card.id}>
                    <Card overflowHidden>
                      <div className="bg-surface-base-inverse-primary relative flex h-full flex-col justify-between p-5">
                        <div className="flex h-full flex-col justify-between">
                          <div className="flex flex-col gap-2">
                            {card.title && (
                              <Typography
                                variant={card.titleVariant ?? 'heading-l'}
                              >
                                {card.title}
                              </Typography>
                            )}
                            {card.description && (
                              <Sanitizer
                                as="span"
                                htmlContent={card.description}
                                className={cn(
                                  '[&_a]:text-text-base-primary [&_a]:underline',
                                  card.descriptionVariant
                                    ? TYPOGRAPHY_CLASS_BY_VARIANT_MAP[
                                        card.descriptionVariant
                                      ]
                                    : 'typo-text-base',
                                )}
                              />
                            )}
                          </div>

                          {(card.mainButton?.label ||
                            card.secondaryButton?.label) && (
                            <div className="z-1 flex">
                              <div className="flex w-full flex-col gap-2">
                                <ButtonsElement card={card} />
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Декоративная графика */}
                        {(ctaMobileImageData || ctaMobileImageUrl) && (
                          <div
                            className={
                              ctaMobileImageClassName ??
                              'pointer-events-none absolute top-[58px] left-0 w-full object-cover min-[390px]:top-[50px] min-[430px]:top-[31px]'
                            }
                          >
                            {ctaMobileImageData ? (
                              <AppImageClient
                                {...ctaMobileImageData}
                                alt={card.title}
                              />
                            ) : (
                              <img src={ctaMobileImageUrl} alt={card.title} />
                            )}
                          </div>
                        )}
                      </div>
                    </Card>
                  </SwiperSlide>
                );
              }

              const {
                id,
                icon,
                title,
                description,
                buttonHref,
                buttonClassName,
                buttonLabel,
                buttonSpacing,
                buttonTarget,
              } = card;

              return (
                <SwiperSlide key={id}>
                  <Card rounded="16" overflowHidden>
                    <div className="flex size-full flex-col justify-between p-5">
                      <div className="flex flex-col gap-2">
                        {title && (
                          <Typography variant="heading-s">{title}</Typography>
                        )}
                        {description && (
                          <Sanitizer
                            as="span"
                            htmlContent={description}
                            className={cn(
                              '[&_a]:text-text-base-primary [&_a]:underline',
                              card.descriptionVariant
                                ? TYPOGRAPHY_CLASS_BY_VARIANT_MAP[
                                    card.descriptionVariant
                                  ]
                                : 'typo-text-base',
                            )}
                          />
                        )}
                      </div>
                      <div className="flex justify-between">
                        <div
                          className={cn(
                            'flex items-center justify-center',
                            iconsWrapperClassName ?? 'h-12 w-12',
                          )}
                        >
                          {icon}
                        </div>
                        {buttonLabel && (
                          <Button
                            href={buttonHref}
                            target={buttonTarget ?? '_blank'}
                            variant="secondary"
                            spacing={buttonSpacing}
                            className={cn('justify-center', buttonClassName)}
                          >
                            {buttonLabel}
                          </Button>
                        )}
                      </div>
                    </div>
                  </Card>
                </SwiperSlide>
              );
            })}

            {customCard && <SwiperSlide>{customCard}</SwiperSlide>}
          </Swiper>
        </div>

        {/* ----------- Десктоп версия ----------- */}
        <div className={gridClasses}>
          {cards.map((card) => {
            if (isCtaCard(card)) {
              return (
                <div
                  key={card.id}
                  className="md:col-span-1"
                  style={{
                    minHeight: desktopCardHeight,
                    gridColumn: `span ${card.colSpan}`,
                  }}
                >
                  <Card
                    overflowHidden
                    style={{ minHeight: card.height ?? desktopCardHeight }}
                  >
                    <div className="bg-surface-base-inverse-primary relative flex h-full flex-col justify-between p-6">
                      {/* Декоративная графика */}
                      {(ctaImageData || ctaImageUrl) && (
                        <div
                          className={
                            ctaImageClassName ??
                            'pointer-events-none absolute top-[10px] left-[-122px] h-full w-[927px] rotate-[-4deg]'
                          }
                        >
                          {ctaImageData ? (
                            <AppImageClient
                              {...ctaImageData}
                              className="h-full w-auto"
                              alt={card.title}
                            />
                          ) : (
                            <img
                              className="h-full"
                              src={ctaImageUrl}
                              alt={card.title}
                            />
                          )}
                        </div>
                      )}

                      <div className="flex h-full flex-col justify-between">
                        <div className="flex flex-col gap-2">
                          {card.title && (
                            <Typography
                              className="max-w-[468px]"
                              variant={card.titleVariant ?? 'heading-l'}
                            >
                              {card.title}
                            </Typography>
                          )}
                          {card.description && (
                            <Sanitizer
                              as="p"
                              htmlContent={card.description}
                              className={cn(
                                '[&_a]:text-text-base-primary [&_a]:underline',
                                card.descriptionVariant
                                  ? TYPOGRAPHY_CLASS_BY_VARIANT_MAP[
                                      card.descriptionVariant
                                    ]
                                  : 'typo-text-base',
                              )}
                            />
                          )}
                        </div>

                        {(card.mainButton?.label ||
                          card.secondaryButton?.label) && (
                          <div className="flex">
                            <div className="flex flex-col gap-3">
                              <ButtonsElement card={card} />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </Card>
                </div>
              );
            }

            const { id, title, description, icon, colSpan } = card;

            return (
              <Card
                key={id}
                overflowHidden
                style={colSpan ? { gridColumn: `span ${colSpan}` } : undefined}
              >
                <div
                  className="flex flex-col justify-between p-6"
                  style={{ minHeight: card.height ?? desktopCardHeight }}
                >
                  <div className="flex flex-col gap-2">
                    {title && (
                      <Typography variant="heading-s">{title}</Typography>
                    )}
                    {description && (
                      <Sanitizer
                        as="p"
                        htmlContent={description}
                        className={cn(
                          '[&_a]:text-text-base-primary [&_a]:underline',
                          card.descriptionVariant
                            ? TYPOGRAPHY_CLASS_BY_VARIANT_MAP[
                                card.descriptionVariant
                              ]
                            : 'typo-text-base',
                        )}
                      />
                    )}
                  </div>
                  <div className="flex justify-between">
                    <div
                      className={cn(
                        'flex items-center justify-center',
                        iconsWrapperClassName ?? 'h-12 w-12',
                      )}
                    >
                      {icon}
                    </div>
                    {card.buttonLabel && (
                      <Button
                        href={card.buttonHref}
                        target={card.buttonTarget ?? '_blank'}
                        variant="secondary"
                        spacing={card.buttonSpacing}
                        className="justify-center"
                      >
                        {card.buttonLabel}
                      </Button>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}

          {customCard && (
            <div className="md:col-span-1 lg:col-span-1">{customCard}</div>
          )}
        </div>
      </>
    );
  },
);

WhyChoosePlatform.displayName = 'WhyChoosePlatform';

const ButtonsElement = memo(({ card }: { card: CTACard }) => {
  return (
    <CTAButtons
      mainButton={{
        label: card.mainButton?.label,
        href: card.mainButton?.href,
        variant: card.mainButton?.variant ?? 'secondary',
        target: card.mainButton?.target ?? '_blank',
        spacing: card.mainButton?.spacing,
        className: `w-full ${card.mainButton?.className ?? ''}`.trim(),
      }}
      secondaryButton={{
        label: card.secondaryButton?.label,
        href: card.secondaryButton?.href,
        variant: card.secondaryButton?.variant ?? 'fourth',
        target: card.secondaryButton?.target ?? '_blank',
        spacing: card.secondaryButton?.spacing,
        className: `w-full ${card.secondaryButton?.className ?? ''}`.trim(),
      }}
    />
  );
});

ButtonsElement.displayName = 'WhyChoosePlatformButtonsElement';
